import { getNavbarAppHref } from '../../../components/navbar/Navbar'
import { useHasFeatureFlag } from '../../../hooks/useHasFeatureFlag'
import { useHasUserCapability } from '../../../hooks/useHasUserCapability'
import { getWorkspaceEncounterHref } from './workspaceEncounterHrefs'

export interface AppEncounterHrefOptions {
  encounterId: string
  orgSlug: string
}

export interface AppEncounterListHrefOptions {
  orgSlug: string
}

export function getAppEncounterHref({
  encounterId,
  orgSlug,
}: AppEncounterHrefOptions) {
  const path = `/${orgSlug}/encounters/${encounterId}`
  return getNavbarAppHref({ app: 'app', path })
}

export function getAppEncounterListHref({
  orgSlug,
}: AppEncounterListHrefOptions) {
  const path = `/${orgSlug}/encounters`
  return getNavbarAppHref({ app: 'app', path })
}

export function usePracticeEncounterHrefs() {
  const canOpenPracticeEncounterDetails = useCanOpenPracticeEncounterDetails()
  const canOpenWorkspaceEncounterDetails = useCanOpenWorkspaceEncounterDetails()
  const canViewEncountersList = useHasUserCapability('encounter.list.view')
  const canOpenAppEncounterList = canViewEncountersList
  const canOpenPracticeEncounterList = canViewEncountersList

  const getPracticeEncounterHref = ({
    encounterId,
    orgSlug,
  }: AppEncounterHrefOptions) => {
    if (canOpenPracticeEncounterDetails) {
      return getAppEncounterHref({ encounterId, orgSlug })
    }

    if (canOpenWorkspaceEncounterDetails) {
      return getWorkspaceEncounterHref({ encounterId, orgSlug })
    }

    return undefined
  }

  const getPracticeEncounterListHref = ({
    orgSlug,
  }: AppEncounterListHrefOptions) => {
    return getAppEncounterListHref({ orgSlug })
  }

  return {
    canOpenAppEncounterList,
    canOpenPracticeEncounterList,
    getPracticeEncounterHref,
    getPracticeEncounterListHref,
  }
}

export function useCanOpenPracticeEncounterDetails() {
  const hasPracticeEncounterDetails = useHasFeatureFlag(
    'enable_practice_encounter_details',
  )
  const canViewEncounterDetails = useHasUserCapability('encounter.details.view')
  return hasPracticeEncounterDetails && canViewEncounterDetails
}

export function useCanOpenWorkspaceEncounterDetails() {
  const canViewBpo = useHasUserCapability('app.bpo.view')
  const canViewEncounterDetails = useHasUserCapability('encounter.details.view')
  const canViewWorkspace = useHasUserCapability('app.workspace.view')
  return canViewEncounterDetails && (canViewWorkspace || canViewBpo)
}
